import { Monitor, Cloud, Globe, ArrowRight } from 'lucide-react';
import { ScrollReveal, ScrollRevealStagger } from '@/components/common/ScrollReveal';

const SYNC_STEPS = [
  {
    num: '01',
    icon: Monitor,
    title: 'PCで整理',
    description: 'デスクトップアプリが写真を自動スキャン。メタデータを抽出し、整理します。',
  },
  {
    num: '02',
    icon: Cloud,
    title: 'クラウドに同期',
    description: '選んだ写真をワンクリックでクラウドにアップロード。メタデータも一緒に保存。',
  },
  {
    num: '03',
    icon: Globe,
    title: 'どこからでもアクセス',
    description: 'ブラウザやスマホから、アップロードした写真をいつでも閲覧・共有できます。',
  },
] as const;

/**
 * SyncFlowSection
 * Desktop→Cloud→Webの3ステップフローセクション
 * アイコンベース（アプリモックアップなし）
 */
export function SyncFlowSection() {
  return (
    <section className="bg-background px-6 md:px-16 lg:px-24 py-24 md:py-32">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-20">
          <ScrollReveal direction="up">
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-4">
              ローカルからクラウドへ、瞬時に
            </h2>
          </ScrollReveal>
          <ScrollReveal direction="up" delay={0.1}>
            <p className="text-base text-muted-foreground max-w-xl mx-auto leading-relaxed">
              デスクトップアプリで整理した写真はクラウドに自動同期。スマホやブラウザからいつでもアクセスできます。
            </p>
          </ScrollReveal>
        </div>

        {/* Steps */}
        <ScrollRevealStagger staggerDelay={0.2}>
          <div className="grid md:grid-cols-[1fr_auto_1fr_auto_1fr] gap-0 items-start">
            {SYNC_STEPS.map((step, index) => {
              const Icon = step.icon;
              const isLast = index === SYNC_STEPS.length - 1;

              return (
                <>
                  {/* Step */}
                  <div key={step.num} className="text-center px-6">
                    {/* Icon Container */}
                    <div className="w-30 h-30 mx-auto mb-6 flex items-center justify-center border border-border rounded-full bg-muted">
                      <Icon className="w-12 h-12 text-accent" strokeWidth={1.5} />
                    </div>
                    {/* Number */}
                    <div className="text-xs font-bold text-accent mb-2 tabular-nums">
                      {step.num}
                    </div>
                    {/* Title */}
                    <h4 className="text-lg font-semibold mb-2">{step.title}</h4>
                    {/* Description */}
                    <p className="text-[13px] text-muted-foreground leading-relaxed">
                      {step.description}
                    </p>
                  </div>

                  {/* Arrow (except after last step) */}
                  {!isLast && (
                    <div
                      key={`arrow-${index}`}
                      className="flex items-center justify-center pt-20 max-md:hidden text-muted-foreground"
                    >
                      <ArrowRight className="w-8 h-8" strokeWidth={1.5} />
                    </div>
                  )}
                </>
              );
            })}
          </div>
        </ScrollRevealStagger>
      </div>
    </section>
  );
}
